import React from "react";

import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";
import { cn } from "../../lib/utils";

// Bloco de repetição, igual no agendamento de mensagem e na campanha — as
// duas telas repetem do mesmo jeito, e duas cópias divergiriam no primeiro
// ajuste. O formato gravado é o que o motor do backend lê
// (services/AgendamentoService/Recorrencia.ts).

const DIAS = [
	{ valor: 0, letra: "D", nome: "domingo" },
	{ valor: 1, letra: "S", nome: "segunda" },
	{ valor: 2, letra: "T", nome: "terça" },
	{ valor: 3, letra: "Q", nome: "quarta" },
	{ valor: 4, letra: "Q", nome: "quinta" },
	{ valor: 5, letra: "S", nome: "sexta" },
	{ valor: 6, letra: "S", nome: "sábado" },
];

const NOME_DO_TIPO = {
	minutely: "de minuto em minuto",
	hourly: "de hora em hora",
	daily: "todo dia",
	weekly: "toda semana",
	monthly: "todo mês",
	yearly: "todo ano",
};

// "a cada 2 semanas", "a cada 30 minutos" — o plural certo por tipo.
const UNIDADE = {
	minutely: ["minuto", "minutos"],
	hourly: ["hora", "horas"],
	daily: ["dia", "dias"],
	weekly: ["semana", "semanas"],
	monthly: ["mês", "meses"],
	yearly: ["ano", "anos"],
};

// A frase que aparece embaixo. Ler "toda segunda e quarta às 9h, até 30/06"
// evita o erro clássico de configurar a repetição e só descobrir o que foi
// combinado quando a mensagem chega no dia errado.
const resumir = (regra) => {
	if (!regra) return null;

	const partes = [];

	if (regra.tipo === "weekly" && regra.diasDaSemana?.length) {
		const nomes = DIAS.filter((d) => regra.diasDaSemana.includes(d.valor)).map(
			(d) => d.nome
		);
		partes.push(
			`toda ${nomes.slice(0, -1).join(", ")}${
				nomes.length > 1 ? ` e ${nomes[nomes.length - 1]}` : nomes[0]
			}`
		);
	} else if (regra.tipo === "monthly" && regra.diaDoMes === "ultimo") {
		partes.push("todo último dia do mês");
	} else if (regra.tipo === "monthly" && regra.diaDoMes) {
		partes.push(`todo dia ${regra.diaDoMes}`);
	} else if (regra.intervalo > 1) {
		const [singular, plural] = UNIDADE[regra.tipo] || ["vez", "vezes"];
		partes.push(
			`a cada ${regra.intervalo} ${regra.intervalo === 1 ? singular : plural}`
		);
	} else {
		partes.push(NOME_DO_TIPO[regra.tipo]);
	}

	if (regra.horaInicio && regra.horaFim) {
		partes.push(`somente entre ${regra.horaInicio} e ${regra.horaFim}`);
	}

	if (regra.terminaEm) {
		const [ano, mes, dia] = regra.terminaEm.split("-");
		partes.push(`até ${dia}/${mes}/${ano}`);
	} else if (regra.maxOcorrencias) {
		partes.push(`por ${regra.maxOcorrencias} vezes`);
	}

	return partes.join(", ");
};

const RecorrenciaFields = ({
	recurrence,
	onRecurrenceChange,
	sendIfMissed,
	onSendIfMissedChange,
	// O texto do "recuperar" muda entre mensagem e campanha.
	rotuloRecuperacao = "Enviar mesmo se o horário já tiver passado",
}) => {
	const regra = recurrence || null;

	const alterar = (patch) => onRecurrenceChange({ ...regra, ...patch });

	const trocarTipo = (tipo) => {
		if (tipo === "nao") {
			onRecurrenceChange(null);
			return;
		}
		// Campos de outro tipo não podem sobreviver à troca: dias da semana
		// numa regra mensal viram lixo que o normalizador do backend descarta,
		// mas que a tela continuaria mostrando.
		const { diasDaSemana, diaDoMes, ...comum } = regra || {};
		// Minuto, hora, dia e semana usam os dias marcados (nos três primeiros
		// como filtro); trocar entre eles não pode apagar a escolha.
		const usaDias = ["minutely", "hourly", "daily", "weekly"].includes(tipo);
		onRecurrenceChange({
			...comum,
			tipo,
			...(usaDias && diasDaSemana?.length ? { diasDaSemana } : {}),
		});
	};

	const alternarDia = (dia) => {
		const atuais = regra?.diasDaSemana || [];
		const novos = atuais.includes(dia)
			? atuais.filter((d) => d !== dia)
			: [...atuais, dia].sort((a, b) => a - b);

		alterar({ diasDaSemana: novos });
	};

	const terminoAtual = (() => {
		if (regra?.terminaEm) return "data";
		if (regra?.maxOcorrencias) return "vezes";
		return "nunca";
	})();

	return (
		<div className="space-y-3 rounded-lg border p-3">
			<div className="space-y-1.5">
				<Label>Repetir</Label>
				<Select value={regra?.tipo || "nao"} onValueChange={trocarTipo}>
					<SelectTrigger>
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="nao">Não repetir</SelectItem>
						<SelectItem value="minutely">De minuto em minuto</SelectItem>
						<SelectItem value="hourly">De hora em hora</SelectItem>
						<SelectItem value="daily">Todo dia</SelectItem>
						<SelectItem value="weekly">Toda semana</SelectItem>
						<SelectItem value="monthly">Todo mês</SelectItem>
						<SelectItem value="yearly">Todo ano</SelectItem>
					</SelectContent>
				</Select>
			</div>

			{regra && (
				<div className="space-y-1.5">
					<Label htmlFor="intervaloDaRepeticao">A cada quantos</Label>
					<div className="flex items-center gap-2">
						<Input
							id="intervaloDaRepeticao"
							type="number"
							min={1}
							className="w-24"
							value={regra.intervalo || 1}
							onChange={(e) =>
								alterar({ intervalo: Math.max(1, +e.target.value || 1) })
							}
						/>
						<span className="text-sm text-muted-foreground">
							{(UNIDADE[regra.tipo] || ["vez", "vezes"])[
								(regra.intervalo || 1) === 1 ? 0 : 1
							]}
						</span>
					</div>
					<p className="text-xs text-muted-foreground">
						1 repete sempre. 2 pula uma vez — “a cada 2 semanas”.
					</p>
				</div>
			)}

			{regra?.tipo === "weekly" && (
				<div className="space-y-1.5">
					<Label>Em quais dias</Label>
					<div className="flex gap-1">
						{DIAS.map((dia) => (
							<button
								key={dia.valor}
								type="button"
								aria-label={dia.nome}
								aria-pressed={Boolean(regra.diasDaSemana?.includes(dia.valor))}
								onClick={() => alternarDia(dia.valor)}
								className={cn(
									"h-8 w-8 rounded-full border text-xs font-medium transition-colors",
									regra.diasDaSemana?.includes(dia.valor)
										? "border-primary bg-primary text-primary-foreground"
										: "border-input hover:bg-accent"
								)}
							>
								{dia.letra}
							</button>
						))}
					</div>
					<p className="text-xs text-muted-foreground">
						Sem nenhum dia marcado, repete no mesmo dia da semana da data
						escolhida.
					</p>
				</div>
			)}

			{(regra?.tipo === "minutely" ||
				regra?.tipo === "hourly" ||
				regra?.tipo === "daily") && (
				<div className="space-y-1.5">
					<Label>Só nestes dias da semana (opcional)</Label>
					<div className="flex gap-1">
						{DIAS.map((dia) => (
							<button
								key={dia.valor}
								type="button"
								aria-label={dia.nome}
								aria-pressed={Boolean(regra.diasDaSemana?.includes(dia.valor))}
								onClick={() => alternarDia(dia.valor)}
								className={cn(
									"h-8 w-8 rounded-full border text-xs font-medium transition-colors",
									regra.diasDaSemana?.includes(dia.valor)
										? "border-primary bg-primary text-primary-foreground"
										: "border-input hover:bg-accent"
								)}
							>
								{dia.letra}
							</button>
						))}
					</div>
					<p className="text-xs text-muted-foreground">
						Nenhum marcado repete todos os dias. Marcando de segunda a sexta,
						fim de semana fica de fora.
					</p>
				</div>
			)}

			{regra?.tipo === "monthly" && (
				<div className="space-y-1.5">
					<Label>Em qual dia do mês</Label>
					<Select
						value={regra.diaDoMes ? String(regra.diaDoMes) : "mesmo"}
						onValueChange={(v) =>
							alterar({
								diaDoMes:
									v === "mesmo" ? undefined : v === "ultimo" ? "ultimo" : +v,
							})
						}
					>
						<SelectTrigger>
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="mesmo">O mesmo dia da data escolhida</SelectItem>
							<SelectItem value="ultimo">O último dia do mês</SelectItem>
							{Array.from({ length: 31 }, (_, i) => i + 1).map((dia) => (
								<SelectItem key={dia} value={String(dia)}>
									Dia {dia}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					<p className="text-xs text-muted-foreground">
						Mês curto não escorrega: dia 31 cai no dia 28 em fevereiro e volta
						para 31 no mês seguinte.
					</p>
				</div>
			)}

			{regra && (
				<>
					<div className="space-y-1.5">
						<Label>Só disparar entre (opcional)</Label>
						<div className="flex items-center gap-2">
							<Input
								type="time"
								aria-label="Início da janela"
								value={regra.horaInicio || ""}
								onChange={(e) =>
									alterar({ horaInicio: e.target.value || undefined })
								}
							/>
							<span className="text-sm text-muted-foreground">e</span>
							<Input
								type="time"
								aria-label="Fim da janela"
								value={regra.horaFim || ""}
								onChange={(e) => alterar({ horaFim: e.target.value || undefined })}
							/>
						</div>
						<p className="text-xs text-muted-foreground">
							Fora dessa faixa o envio espera a janela abrir, em vez de sair na
							madrugada.
						</p>
					</div>

					<div className="space-y-1.5">
						<Label>Até quando</Label>
						<Select
							value={terminoAtual}
							onValueChange={(v) =>
								alterar({
									terminaEm: v === "data" ? regra.terminaEm || "" : undefined,
									maxOcorrencias:
										v === "vezes" ? regra.maxOcorrencias || 10 : undefined,
								})
							}
						>
							<SelectTrigger>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="nunca">Sem data para parar</SelectItem>
								<SelectItem value="data">Até uma data</SelectItem>
								<SelectItem value="vezes">Depois de um número de vezes</SelectItem>
							</SelectContent>
						</Select>

						{terminoAtual === "data" && (
							<Input
								type="date"
								aria-label="Repetir até"
								value={regra.terminaEm || ""}
								onChange={(e) => alterar({ terminaEm: e.target.value })}
							/>
						)}

						{terminoAtual === "vezes" && (
							<Input
								type="number"
								min={1}
								aria-label="Número de vezes"
								value={regra.maxOcorrencias || 1}
								onChange={(e) =>
									alterar({ maxOcorrencias: Math.max(1, +e.target.value) })
								}
							/>
						)}
					</div>

					<p className="rounded-md bg-muted p-2 text-xs">
						Repete <span className="font-medium">{resumir(regra)}</span>
						{regra.tipo === "minutely" || regra.tipo === "hourly"
							? ", a partir da data e hora escolhidas acima."
							: ", sempre no horário escolhido acima."}
					</p>
				</>
			)}

			<div className="flex items-center justify-between gap-3 border-t pt-3">
				<div className="space-y-0.5">
					<Label htmlFor="sendIfMissed">{rotuloRecuperacao}</Label>
					<p className="text-xs text-muted-foreground">
						Desligado, o que perdeu a hora por mais de 15 minutos é pulado — o
						sistema não manda de madrugada só porque ficou fora do ar.
					</p>
				</div>
				<Switch
					id="sendIfMissed"
					checked={Boolean(sendIfMissed)}
					onCheckedChange={onSendIfMissedChange}
				/>
			</div>
		</div>
	);
};

export default RecorrenciaFields;
